import { FC, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useFormik } from 'formik';
import * as yup from 'yup';
import { useService } from '@hooks';
import { RxUtils, Tr } from '@utils';
import { AdditionalService, additionalServiceService, PackageDeal, packageDealService } from '@services';
import { Grid, Stack } from '@mui/material';
import { FormikTextfield, ImageUpload, Searchbox } from '@components/common';
import { FormStepLayout, FormStepQuestion } from '@components/portal/common';
import { PackageDealListViewer } from '../PackageDealListViewer';
import { ReactComponent as Illustration } from '@assets/illustrations/no-stock-added.svg';

type FormFields = Extendable<{
	name: string;
	summary: string;
	description: string;
	services: AdditionalService[];
}>;

export interface IAddEditPackageFormProps {
	values?: Partial<PackageDeal>;
	onSubmit?: (values: Partial<PackageDeal>, newLogo?: File) => void;
}

export const AddEditPackageForm: FC<IAddEditPackageFormProps> = ({ values, onSubmit }) => {
	const { t } = useTranslation('portal');
	const [imagesChanged, setImagesChanged] = useState(false);
	const [currentLogo, setCurrentLogo] = useState<string[]>([]);
	const [newLogo, setNewLogo] = useState<File>();
	const [selectedServices, setSelectedServices] = useState<AdditionalService[]>([]);
	const [searchText, setSearchText] = useState('');
	const [searchResult, setSearchResult] = useState<AdditionalService[]>([]);
	const { data: additionalServices = [] } = useService(() => additionalServiceService.getAll(), []);
	const displayingEntities = searchText ? searchResult : additionalServices;

	useEffect(() => {
		values?.id && RxUtils.promisify(packageDealService.getLogo(values as PackageDeal), (logo) => setCurrentLogo(logo ? [logo] : []));
	}, [values]);

	useEffect(() => setSelectedServices(values?.services || []), [values?.services]);
	useEffect(() => {
		if (searchText) {
			RxUtils.promisify(additionalServiceService.search(searchText), setSearchResult);
		} else {
			setSearchResult([]);
		}
	}, [searchText]);

	const formik = useFormik<FormFields>({
		initialValues: { name: values?.name || '', summary: values?.summary || '', description: values?.description || '' },
		enableReinitialize: true,
		validateOnChange: true,
		validateOnMount: true,
		validationSchema: yup.object({
			name: yup.string().required(t('field-required')),
			summary: yup.string().optional(),
			description: yup.string().optional()
		}),
		onSubmit: ({ name, summary, description }, api) => {
			onSubmit?.({ name, summary, description, services: selectedServices }, newLogo);
			api.setSubmitting(false);
		}
	});
	const { submitForm, handleSubmit } = formik;
	const disabled = imagesChanged ? false : !formik.isValid || formik.isSubmitting || !selectedServices.length;

	return (
		<form onSubmit={handleSubmit} style={{ flexGrow: 1, overflowY: 'hidden' }}>
			<FormStepLayout
				primaryButtonProps={{
					disabled,
					children: (
						<Tr.Portal
							path={values ? 'package-deal.add-edit-package.update-package' : 'package-deal.add-edit-package.add-package'}
						/>
					),
					onClick: submitForm,
					'data-testid': 'add-edit-package-next-button'
				}}
				hideSecondaryButton
			>
				<Stack direction="column" spacing={8}>
					<FormStepQuestion
						title={<Tr.Portal path="package-deal.add-edit-package.package-name-section" />}
						titleProps={{ variant: 'h4' }}
					>
						<Grid container rowSpacing={5} columnSpacing={4}>
							<Grid item xs={4}>
								<FormikTextfield
									formikApi={formik}
									name="name"
									title={t('package-deal.add-edit-package.package-name')}
									placeholder={t('package-deal.add-edit-package.package-name-placeholder')}
									required
								/>
							</Grid>
							<Grid item xs={4}>
								<FormikTextfield
									formikApi={formik}
									name="summary"
									title={t('package-deal.add-edit-package.package-summary')}
									placeholder={t('package-deal.add-edit-package.package-summary-placeholder')}
								/>
							</Grid>
							<Grid item xs={4}>
								<FormikTextfield
									formikApi={formik}
									name="description"
									title={t('package-deal.add-edit-package.package-description')}
									placeholder={t('package-deal.add-edit-package.package-description-placeholder')}
								/>
							</Grid>
							<Grid item xs={4}>
								<ImageUpload
									title={<Tr.Portal path="package-deal.logo" />}
									images={currentLogo}
									onChange={(files) => (setNewLogo(files?.[0]), setImagesChanged(true))}
								/>
							</Grid>
						</Grid>
					</FormStepQuestion>
					<FormStepQuestion
						title={<Tr.Portal path="package-deal.add-edit-package.select-services" />}
						titleProps={{ variant: 'h4', sx: { marginBottom: 0 } }}
						titleSectionProps={{ alignItems: 'center' }}
						rightSection={
							<Searchbox
								testId="add-edit-package-deal-search-box"
								placeholder={t('package-deal.search-placeholder-services')}
								searchText={searchText}
								onSearchTextChange={setSearchText}
							/>
						}
					>
						<PackageDealListViewer
							entityType="service"
							showResultCount={!!searchText}
							entities={displayingEntities}
							selected={displayingEntities.filter(({ id }) =>
								selectedServices?.find((selectedService) => id === selectedService.id)
							)}
							empty={<Illustration />}
							onSelectChange={(entites) => setSelectedServices(entites as AdditionalService[])}
							sx={{ marginX: -2, paddingRight: 2 }}
						/>
					</FormStepQuestion>
				</Stack>
			</FormStepLayout>
		</form>
	);
};
